import {
  Document,
  BLOCKS,
  INLINES,
  Inline,
  Text as TextType,
} from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

import { Text, TextProps } from '@awaze/design-system.components.text'
import { pxToRems } from '@awaze/design-system.utils.react'
import { LinkModule } from '@/components/links/link.module'
import styled from 'styled-components'
import { JSX, ReactNode } from 'react'

interface RichTextProps {
  content?: Document
  textColor?: TextProps['color']
  className?: string
}

const Container = styled.div`
  p + p {
    margin-top: ${pxToRems(12)};
  }
`

const TableStyled = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: ${pxToRems(16)} 0;
`

const TableRowStyled = styled.tr``

const TableCellStyled = styled.td`
  border: 1px solid var(--color-black-opacity-10);
  padding: ${pxToRems(8)};
`

const TableHeaderCellStyled = styled.th`
  border: 1px solid var(--color-black-opacity-10);
  padding: ${pxToRems(8)};
  background: var(--color-black-opacity-05);
  font-weight: bold;
  text-align: left;
`

const renderTableRowNode = (_: unknown, children: ReactNode) => (
  <TableRowStyled>{children}</TableRowStyled>
)

const renderTableCellNode = (_: unknown, children: ReactNode) => (
  <TableCellStyled>{children}</TableCellStyled>
)

const renderTableHeaderCellNode = (_: unknown, children: ReactNode) => (
  <TableHeaderCellStyled>{children}</TableHeaderCellStyled>
)

const renderTableNode = (_: unknown, children: ReactNode) => (
  <TableStyled>
    <tbody>{children}</tbody>
  </TableStyled>
)

export default function RichText({
  content,
  textColor,
  className,
}: RichTextProps): JSX.Element {
  return (
    <Container className={className}>
      {documentToReactComponents(content, {
        renderText: (text) => {
          return text.split('\n').reduce((children, textSegment, index) => {
            return [...children, index > 0 && <br key={index} />, textSegment]
          }, [])
        },
        renderNode: {
          [BLOCKS.PARAGRAPH]: (_, children) => {
            return (
              <Text as="p" sizes={300} color={textColor}>
                {children}
              </Text>
            )
          },
          [BLOCKS.UL_LIST]: (_, children) => {
            return (
              <ul
                style={{
                  margin: `${pxToRems(8)} 0 ${pxToRems(8)} ${pxToRems(20)}`,
                }}
              >
                {children}
              </ul>
            )
          },
          [BLOCKS.TABLE]: renderTableNode,
          [BLOCKS.TABLE_ROW]: renderTableRowNode,
          [BLOCKS.TABLE_CELL]: renderTableCellNode,
          [BLOCKS.TABLE_HEADER_CELL]: renderTableHeaderCellNode,
          [INLINES.HYPERLINK]: (node: Inline) => {
            let urlText = (node.content[0] as TextType)?.value
            const noFollow = urlText?.includes('{{nofollow}}')
            urlText = urlText?.replace('{{nofollow}}', '')

            return (
              urlText && (
                <LinkModule
                  href={node.data.uri}
                  {...(noFollow ? { rel: 'nofollow' } : {})}
                >
                  {urlText}
                </LinkModule>
              )
            )
          },
        },
      })}
    </Container>
  )
}
